/**
 * apiClient.js
 * * Central utility for making authenticated requests to the RentalAPI.
 */

const API_BASE_URL = "https://rentalapi.ccaccacademy.org/api"

/**
 * Retrieves the JWT from localStorage.
 * IMPORTANT: In a production app, you might use HttpOnly cookies for better security.
 */
const getToken = () => localStorage.getItem("jwt_token")

/**
 * Generic fetch wrapper for authenticated API calls.
 * @param {string} endpoint - The API endpoint path (e.g., 'Account/login', 'Booking/current').
 * @param {object} options - Standard fetch options (method, body, headers).
 * @returns {Promise<object>} - The JSON response data.
 */
const apiClient = async (endpoint, options = {}) => {
  const token = getToken()
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  }

  // Attach the JWT for secured endpoints
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    headers: headers,
  })

  // Handle successful response with JSON body
  if (response.ok) {
    // Check for empty content (e.g., successful POST/PUT/DELETE with no body)
    const contentType = response.headers.get("content-type")
    if (contentType && contentType.includes("application/json")) {
      return response.json()
    }
    return {} // Return empty object for success with no content
  }

  // Handle errors
  const errorData = await response
    .json()
    .catch(() => ({ message: "An unknown error occurred" }))

  // Throw a detailed error for centralized handling in components
  throw new Error(errorData.message || `API Error: ${response.statusText}`)
}

export const get = async endpoint => apiClient(endpoint, { method: "GET" })
// Function to handle PUT requests (used for updating profile data)
export const put = async (endpoint, data) =>
  apiClient(endpoint, { method: "PUT", body: JSON.stringify(data) })

// Function to handle POST requests (used for password changes, etc.)
export const post = async (endpoint, data) =>
  apiClient(endpoint, { method: "POST", body: JSON.stringify(data) })

// Function to handle login (does not need the JWT yet)
export const login = async credentials => {
  const response = await apiClient("Account/login", {
    method: "POST",
    body: JSON.stringify(credentials),
    // Explicitly set Authorization to undefined so an old token isn't accidentally sent
    headers: { Authorization: undefined },
  })

  // Store the JWT upon successful login
  if (response.token) {
    localStorage.setItem("jwt_token", response.token)
  }
  return response
}

// Function to handle token registration (does not need the JWT yet)
export const registerWithToken = async data => {
  return apiClient("Account/register-with-token", {
    method: "POST",
    body: JSON.stringify(data),
    headers: { Authorization: undefined },
  })
}

// Function to remove token on logout
export const logout = () => {
  localStorage.removeItem("jwt_token")
  // You might want to remove user data state here too
}

export const updateProfile = async profileData =>
  put("Account/profile", profileData)

export const changePassword = async passwordData =>
  post("Account/password", passwordData)

export const forgotPassword = async email =>
  post("Account/forgot-password", { email })

export const resetPassword = async data =>
  post("Account/reset-password", data)

export const getCurrentBookings = async () => get("Booking/current")

/**
 * Gets the detailed information for a single booking, including all instances.
 * @param {string|number} bookingId The ID of the booking to fetch.
 * @returns {Promise<object>} The detailed booking object.
 */
export const getBookingDetails = async bookingId => {
  return get(`Booking/${bookingId}`) // Calls GET /api/Booking/{bookingId}
}

/**
 * Sends a request to cancel a specific date of a recurring booking.
 * @param {string|number} bookingId The main booking ID.
 * @param {string} date The date to cancel (e.g., "2025-11-03").
 * @returns {Promise<object>}
 */
export const cancelBookingDate = async (bookingId, date) => {
  // Updated to send 'dateToCancel' to match the C# CancelDateDto
  return post(`Booking/${bookingId}/cancel-date`, { dateToCancel: date })
}

export const getClientInvoices = async () => get("Booking/invoices")

export const getClientPayments = async () => get("Booking/payments")

export const getClientCredits = async () => get("Booking/credits")

export default apiClient
